import React, { useState, useEffect, useCallback, useMemo } from "react";
import Swal from "sweetalert2";
import "./customer.css";
import Hexapi from "../../utility/HexAPI/Hexapi";
import HexTextInput from "../../utility/HexInput/HexTextInput";
import HexSelect from "../../utility/HexInput/HexSelect";
import countrydata from "../../utility/countrycode.json";

import {
    getPhoneErrorMessage,
    getPhoneMaxLength,
    validatePhoneNumber,
} from "../../utility/phoneValidation";
import { decodeUrl } from "./decodeUrl";
import { validateEmail } from "./validations";

const INITIAL_FORM = {
    Marketedby: "",
    companyname: "",
    contactperson: "",
    contactnumber: "",
    email: "",
    leadsourceid: "",
    subleadsource: "",
    countrycodecontactperson: "+91",
};

// Section names that already exist as static sections in the JSX.
// Any dynamic custom field whose SectionName matches one of these
// gets merged into that static section instead of creating a new one.
const STATIC_SECTION_NAMES = ["Customer Details", "Customer Enquiry"];

export default function CustomerEnquiryform() {
    const [form, setForm] = useState(INITIAL_FORM);
    const [customFieldValues, setCustomFieldValues] = useState({});
    const [customFieldsConfig, setCustomFieldsConfig] = useState([]);
    
    const [productData, setProductData] = useState([]);
    const [LeadSource, setLeadSource] = useState([]);
    const [SubLeadSource, setSubLeadSource] = useState([]);
    const [AgentList, setAgentList] = useState([]);

    // ─── Initial Data Fetch ──────────────────────────────────────────────────────
    useEffect(() => {
        let isMounted = true;

        // Fetch Master Data
        Hexapi({
            query: `[dbo].[CRM_Proc_CustomerEnquiry_FormGetData]`,
            queryArr: [],
        })
            .then((resp) => {
                if (!isMounted) return;
                const initialProductData = (resp?.product || []).map((item) => ({
                    productid: item.productid,
                    productname: item.product,
                    checked: false,
                }));

                setLeadSource(resp?.leadsource || []);
                setAgentList(resp?.agent || []);
                setProductData(initialProductData);
            })
            .catch(console.error);

      
        Hexapi({
            query: `[dbo].[CRM_Proc_CustomFields_GetList]`,
            queryArr: [],
        })
            .then((resp) => {
                if (!isMounted) return;
                const fieldsList = resp?.[""] || resp?.customFields || [];
                const activeFields = fieldsList.filter(
                    (f) => (f.isactive ?? f.isActive ?? 1) === 1
                );
                setCustomFieldsConfig(activeFields);

                const initialCustomState = {};
                activeFields.forEach((field) => {
                    const key = field.fieldkey || field.fieldKey;
                    const defaultVal = field.defaultvalue || field.defaultValue || "";
                    if (key) {
                        initialCustomState[key] = defaultVal;
                    }
                });
                setCustomFieldValues(initialCustomState);
            })
            .catch((err) => console.error("Error fetching custom fields:", err));

        decodeUrl(window.location.href).then((res) => {
            if (isMounted && res?.agentID) {
                setForm((prev) => ({ ...prev, Marketedby: res.agentID }));
            }
        });

        return () => {
            isMounted = false;
        };
    }, []);

    const setField = useCallback(
        (field) => (value) => setForm((prev) => ({ ...prev, [field]: value })),
        []
    );

    const handleCustomFieldChange = useCallback((key, value) => {
        setCustomFieldValues((prev) => ({
            ...prev,
            [key]: value,
        }));
    }, []);

    const fetchSubLeadSource = useCallback(async (leadsourceid) => {
        if (!leadsourceid) {
            setSubLeadSource([]);
            setForm((p) => ({ ...p, subleadsource: "" }));
            return;
        }
        try {
            const resp = await Hexapi({
                query: `[dbo].[CRM_Proc_LeadSource_OnChange] @LeadSourceID='{0}'`,
                queryArr: [leadsourceid],
            });

            setSubLeadSource(resp?.subleadsource || []);
        } catch (err) {
            console.error("Error fetching sublead sources:", err);
        }
    }, []);

    const handleLeadSourceChange = useCallback(
        (value) => {
            setForm((prev) => ({ ...prev, leadsourceid: value, subleadsource: "" }));
            fetchSubLeadSource(value);
        },
        [fetchSubLeadSource]
    );

    const handleContactCountryCodeChange = useCallback((value) => {
        setForm((prev) => ({
            ...prev,
            countrycodecontactperson: value,
            contactnumber: "",
        }));
    }, []);

    // ─── Dynamic Field Grouping ──────────────────────────────────────────────────
    // Group all active custom fields by their SectionName first.
    const groupedCustomFields = useMemo(() => {
        return customFieldsConfig.reduce((acc, field) => {
            const section = field.sectionname || field.sectionName || field.section || "Additional Information";
            if (!acc[section]) {
                acc[section] = [];
            }
            acc[section].push(field);
            return acc;
        }, {});
    }, [customFieldsConfig]);

    // Fields whose SectionName matches an existing static section -
    // these render INSIDE that static section, not as a new block.
    const staticSectionCustomFields = useMemo(() => {
        const map = {};
        STATIC_SECTION_NAMES.forEach((name) => {
            map[name] = groupedCustomFields[name] || [];
        });
        return map;
    }, [groupedCustomFields]);

    // Remaining sections (anything not "Customer Details" / "Customer Enquiry")
    // still render as their own dynamic section blocks, same as before.
    const remainingGroupedCustomFields = useMemo(() => {
        const result = {};
        Object.keys(groupedCustomFields).forEach((section) => {
            if (!STATIC_SECTION_NAMES.includes(section)) {
                result[section] = groupedCustomFields[section];
            }
        });
        return result;
    }, [groupedCustomFields]);

    // ─── Product Interest Selection Handler ──────────────────────────────────────
    const handleCheckBox = useCallback((index) => {
        setProductData((prev) =>
            prev.map((item, i) =>
                i === index ? { ...item, checked: !item.checked } : item
            )
        );
    }, []);

    const selectedProductIDs = useMemo(() => {
        return productData
            .filter((p) => p.checked)
            .map((p) => p.productid)
            .join(",");
    }, [productData]);

    // ─── Form Reset Helper ────────────────────────────────────────────────────────
    const resetFormValues = useCallback(() => {
        setForm((prev) => ({
            ...INITIAL_FORM,
            Marketedby: prev.Marketedby,
            countrycodecontactperson: "+91",
        }));
        
        // Reset dynamic field values to defaults
        const defaultCustomState = {};
        customFieldsConfig.forEach((field) => {
            const key = field.fieldkey || field.fieldKey;
            defaultCustomState[key] = field.defaultvalue || field.defaultValue || "";
        });
        setCustomFieldValues(defaultCustomState);

        // Reset Product selection check states without wiping product list
        setProductData((prev) => prev.map((p) => ({ ...p, checked: false })));
        setSubLeadSource([]);
    }, [customFieldsConfig]);

    // ─── Validation Logic ────────────────────────────────────────────────────────
    const validateForm = () => {
        const s = form;
        if (!s.Marketedby) return "Marketed by not selected.";
        if (!s.companyname.trim()) return "Company Name is required.";
        if (!s.contactperson.trim()) return "Contact Person is required.";
        if (!s.countrycodecontactperson) return "Country Code is required.";
        if (!s.contactnumber.trim()) return "Contact Number is required.";
        if (!validatePhoneNumber(s.contactnumber, s.countrycodecontactperson)) {
            return getPhoneErrorMessage("Contact Number", s.countrycodecontactperson);
        }
        if (!s.leadsourceid) return "Lead Source not selected.";
        if (!selectedProductIDs) return "At least one product interest is required.";
        if (s.email && !validateEmail(s.email)) return "Invalid email address.";

        for (const field of customFieldsConfig) {
            const isReq = !!(field.isrequired ?? field.isRequired);
            const key = field.fieldkey || field.fieldKey;
            const label = field.label || key;
            const val = customFieldValues[key];

            if (isReq && (val === undefined || val === null || val === "" || (Array.isArray(val) && val.length === 0))) {
                return `${label} is required.`;
            }
        }

        return null;
    };

    // ─── Query Builder ───────────────────────────────────────────────────────────
    const buildQuery = (forceInsert = false) => {
        const s = form;

        let query = `[dbo].[CRM_Proc_NewLead_SubmitV6]
            @CompanyName='{0}',
            @CustomerName='{1}',
            @ContactNumber='{2}',
            @EmailID='{3}',
            @AgentID='{4}',
            @ProductInterestIDs='{5}',
            @LeadSourceID='{6}',
            @ContactNumberCountryCode='{7}',
            @SubLeadSource='{8}',
            @CustomFields='{9}'`;

        const queryArr = [
            s.companyname.trim(),
            s.contactperson.trim(),
            s.contactnumber.trim(),
            s.email ? s.email.trim() : "",
            s.Marketedby,
            selectedProductIDs,
            s.leadsourceid,
            s.countrycodecontactperson,
            s.subleadsource || "",
            JSON.stringify(customFieldValues),
        ];

        if (forceInsert) {
            query += `, @ForceInsert='{10}'`;
            queryArr.push(1);
        }

        return { query, queryArr };
    };

    const submitToApi = async (forceInsert = false) => {
        const { query, queryArr } = buildQuery(forceInsert);
        const resp = await Hexapi({ query, queryArr });
        
        const leadResponse = resp?.newlead?.[0];

        if (leadResponse?.command === 1) {
            Swal.fire({
                icon: "success",
                title: "Thanks For Your Response",
                text: `Your CustomerID is ${leadResponse.customerid}`,
            }).then((r) => {
                if (r.isConfirmed || r.isDismissed) {
                    resetFormValues();
                }
            });
        } else if (leadResponse?.command === -1 && !forceInsert) {
            Swal.fire({
                title: "Duplicate Entry",
                text: "A record with this information already exists. Do you want to proceed?",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, Submit",
                cancelButtonText: "Cancel",
            }).then(async (r) => {
                if (r.isConfirmed) await submitToApi(true);
            });
        } else {
            Swal.fire({ 
                icon: "error",
                title: "Error",
                text: leadResponse?.msg || "Failed to submit customer details." 
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const error = validateForm();
        if (error) {
            Swal.fire({ icon: "error", title: "Validation Error", text: error });
            return;
        }
        try {
            await submitToApi(false);
        } catch (err) {
            console.error("Submission Error:", err);
            Swal.fire({ icon: "error", title: "Server Error", text: "Something went wrong during submission." });
        }
    };


    const renderCustomField = (field) => {
        const key = field.fieldkey || field.fieldKey;
        const label = field.label;
        const fieldType = (field.fieldtype || field.fieldType || "text").toLowerCase();
        const isReq = !!(field.isrequired ?? field.isRequired);
        const rawOptions = field.optionsjson || field.optionsJSON;

        let parsedOptions = [];
        if (rawOptions) {
            try {
                parsedOptions = typeof rawOptions === "string" ? JSON.parse(rawOptions) : rawOptions;
            } catch {
                parsedOptions = [];
            }
        }

        const value = customFieldValues[key] ?? "";

        switch (fieldType) {
            case "select":
                return (
                    <HexSelect
                        key={key}
                        label={label}
                        required={isReq}
                        value={value}
                        onChange={(val) => handleCustomFieldChange(key, val)}
                        options={parsedOptions.map((opt) => 
                            typeof opt === "object" ? opt : { value: opt, label: opt }
                        )}
                    />
                );

            case "textarea":
                return (
                    <div key={key} className="flex flex-col gap-1.5 col-span-full">
                        <label className="text-sm font-medium text-slate-700">
                            {label} {isReq && <span className="text-red-500">*</span>}
                        </label>
                        <textarea
                            className="w-full border border-slate-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                            rows={3}
                            placeholder={`Enter ${label}`}
                            value={value}
                            onChange={(e) => handleCustomFieldChange(key, e.target.value)}
                        />
                    </div>
                );

            case "radio":
                return (
                    <div key={key} className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-slate-700">
                            {label} {isReq && <span className="text-red-500">*</span>}
                        </label>
                        <div className="flex items-center gap-4">
                            {parsedOptions.map((opt) => {
                                const optVal = typeof opt === "object" ? opt.value : opt;
                                const optLabel = typeof opt === "object" ? opt.label : opt;
                                return (
                                    <label key={optVal} className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                                        <input
                                            type="radio"
                                            name={key}
                                            value={optVal}
                                            checked={value === optVal}
                                            onChange={(e) => handleCustomFieldChange(key, e.target.value)}
                                            className="h-4 w-4 accent-amber-400"
                                        />
                                        {optLabel}
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                );

            case "number":
            case "date":
                return (
                    <HexTextInput
                        key={key}
                        label={label}
                        type={fieldType}
                        placeholder={fieldType === "number" ? `Enter ${label}` : undefined}
                        required={isReq}
                        value={value}
                        onChange={(val) => handleCustomFieldChange(key, val)}
                    />
                );

            case "checkbox":
                return (
                    <div key={key} className="flex flex-col gap-1.5 justify-center">
                        <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700 mt-5">
                            <input
                                type="checkbox"
                                checked={!!value}
                                onChange={(e) => handleCustomFieldChange(key, e.target.checked)}
                                className="h-4 w-4 accent-amber-400"
                            />
                            <span>
                                {label} {isReq && <span className="text-red-500">*</span>}
                            </span>
                        </label>
                    </div>
                );

            case "text":
            default:
                return (
                    <HexTextInput
                        key={key}
                        label={label}
                        placeholder={`Enter ${label}`}
                        required={isReq}
                        value={value}
                        onChange={(val) => handleCustomFieldChange(key, val)}
                    />
                );
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <main
                className="flex-grow overflow-y-auto p-8 no-scrollbar bg-surface"
                style={{ height: "93vh", overflow: "auto", paddingBottom: "10vh" }}
            >
                <div className="max-w-7xl mx-auto space-y-6">
                    {/* Header Controls */}
                    <div className="flex items-center justify-between bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
                        <div className="flex items-center gap-6">
                            <HexSelect
                                label="Marketed by"
                                required
                                disabled
                                value={Number(form.Marketedby)}
                                onChange={setField("Marketedby")}
                                variant="borderless"
                                className="!w-40"
                                options={AgentList.map((s) => ({
                                    value: s.agentid,
                                    label: s.agentname,
                                }))}
                                wrapperClassName="flex flex-col gap-1"
                            />
                        </div>

                        <div className="submit-btn-wrapper">
                            <button type="submit" className="submit-btn">
                                SUBMIT
                            </button>
                        </div>
                    </div>

                    {/* Basic Info Section */}
                    <section className="bg-white p-8 rounded-lg border border-slate-200 shadow-sm">
                        <div className="mb-8 flex items-center gap-4">
                            <h2 className="text-lg font-bold text-slate-800 shrink-0">
                                Customer Details
                            </h2>
                            <div className="h-px w-full bg-slate-100" />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-6">
                            <HexTextInput
                                label="Company Name"
                                placeholder="Enter Company Name"
                                required
                                onChange={setField("companyname")}
                                value={form.companyname}
                            />

                            <HexTextInput
                                label="Contact Person"
                                placeholder="Enter Contact Person"
                                required
                                onChange={setField("contactperson")}
                                value={form.contactperson}
                            />

                            <HexSelect
                                label="Country Code"
                                required
                                showSearch
                                value={form.countrycodecontactperson}
                                onChange={handleContactCountryCodeChange}
                                options={countrydata.map((item) => ({
                                    value: item.dial_code,
                                    label: `${item.dial_code} ${item.name}`,
                                }))}
                            />

                            <HexTextInput
                                label="Contact Number"
                                placeholder="Enter Contact Number"
                                required
                                numericOnly
                                maxLength={getPhoneMaxLength(form.countrycodecontactperson)}
                                showCount
                                onChange={setField("contactnumber")}
                                value={form.contactnumber}
                            />

                            <HexTextInput
                                label="Email ID"
                                placeholder="Enter Email ID"
                                type="email"
                                onChange={setField("email")}
                                value={form.email}
                            />

                            <HexSelect
                                label="Lead Source"
                                required
                                value={form.leadsourceid}
                                onChange={handleLeadSourceChange}
                                showSearch
                                options={LeadSource.map((s) => ({
                                    value: s.leadsourceid,
                                    label: s.leadsource,
                                }))}
                            />

                            <HexSelect
                                label="Sub Lead Source"
                                creatable
                                allowClear
                                disabled={!form.leadsourceid}
                                value={form.subleadsource}
                                onChange={setField("subleadsource")}
                                placeholder="---Select or type---"
                                options={SubLeadSource.map((s) => ({
                                    value: s.subleadsource,
                                    label: s.subleadsource,
                                }))}
                            />

                            {/* Dynamic custom fields whose SectionName = "Customer Details" */}
                            {staticSectionCustomFields["Customer Details"].map((field) =>
                                renderCustomField(field)
                            )}
                        </div>
                    </section>

                    {/* Product Interest Section */}
                    <section className="bg-white p-8 rounded-lg border border-slate-200 shadow-sm">
                        <div className="mb-8 flex items-center gap-4">
                            <h2 className="text-lg font-bold text-slate-800 shrink-0">
                                Customer Enquiry
                            </h2>
                            <div className="h-px w-full bg-slate-100" />
                        </div>

                        <div className="mb-2">
                            <h3 className="text-sm font-bold text-slate-800 mb-6 flex items-center gap-2">
                                Product Interest <span className="text-red-500">*</span>
                            </h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-y-6 gap-x-4">
                                {productData.map((data, index) => (
                                    <label
                                        key={data.productid}
                                        className="flex items-center gap-2 cursor-pointer text-sm text-slate-700"
                                    >
                                        <input
                                            type="checkbox"
                                            name={data.productname}
                                            value={data.productid}
                                            checked={data.checked || false}
                                            onChange={() => handleCheckBox(index)}
                                            className="h-4 w-4 accent-amber-400"
                                        />
                                        {data.productname}
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Dynamic custom fields whose SectionName = "Customer Enquiry" */}
                        {staticSectionCustomFields["Customer Enquiry"].length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-6 mt-6">
                                {staticSectionCustomFields["Customer Enquiry"].map((field) =>
                                    renderCustomField(field)
                                )}
                            </div>
                        )}
                    </section>

                    {/* Remaining Dynamic Custom Field Sections (excluding Customer Details / Customer Enquiry) */}
                    {Object.keys(remainingGroupedCustomFields).map((sectionTitle) => (
                        <section
                            key={sectionTitle}
                            className="bg-white p-8 rounded-lg border border-slate-200 shadow-sm"
                        >
                            <div className="mb-8 flex items-center gap-4">
                                <h2 className="text-lg font-bold text-slate-800 shrink-0">
                                    {sectionTitle}
                                </h2>
                                <div className="h-px w-full bg-slate-100" />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-6">
                                {remainingGroupedCustomFields[sectionTitle].map((field) =>
                                    renderCustomField(field)
                                )}
                            </div>
                        </section>
                    ))}
                </div>
            </main>
        </form>
    );
}